/**
 * Route `/blog/$slug` — lecteur d'article de blog in-app (fil d'actu,
 * migration 0220). Monté directement sous `appRoute` (comme `/dag/$exerciseId`)
 * → accessible à tous les rôles authentifiés sans duplication.
 *
 * Port quasi-verbatim de `apps/marketing/src/app/blog/[slug]/page.tsx`, moins
 * le header sticky marketing (logo+nav) et la section CTA d'acquisition
 * (PhoneFrame/ShowcasePlayer/lien /demo) — inutiles puisqu'on est déjà dans
 * l'app.
 */
import { useParams, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Clock, ExternalLink, Lightbulb } from 'lucide-react';
import {
  articleSocialLinks,
  blogPenseurHref,
  blogResourceHref,
  type BlogResource,
  type BlogResourceKind,
} from '@jeraisonne/db';
import { useBlogArticleBySlug } from '@/lib/blog/use-blog-article';
import { ReflectionJourney } from '@/features/blog-reader/reflection-journey';
import { BlogActiveReaders } from '@/features/blog-reader/blog-active-readers';
import { BlogSocialReactions, BlogAuthorCard } from '@/features/blog-reader/blog-author';

function formatBlogDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function ResourceGroup({
  title,
  items,
  kind,
}: {
  title: string;
  items: BlogResource[];
  kind: BlogResourceKind;
}) {
  if (!items.length) return null;
  return (
    <div>
      <h3 className="text-ink-500 mb-2 text-sm font-bold uppercase tracking-wide">{title}</h3>
      <ul className="space-y-2">
        {items.map((r, i) => (
          <li key={i}>
            <a
              href={blogResourceHref(r, kind)}
              target="_blank"
              rel="noreferrer"
              className="hover:border-primary-300 border-ink-200 group block rounded-xl border bg-white p-3 transition-colors"
            >
              <p className="text-ink-900 flex items-start gap-1.5 text-sm font-semibold">
                <span className="group-hover:underline">
                  {r.title}
                  {r.author && <span className="text-ink-500 font-normal"> — {r.author}</span>}
                </span>
                <ExternalLink className="text-primary-400 mt-0.5 size-3.5 shrink-0" aria-hidden />
              </p>
              <p className="text-ink-500 mt-0.5 text-xs">{r.why}</p>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BlogArticleReaderPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const navigate = useNavigate();
  const { data: article, isLoading } = useBlogArticleBySlug(slug);

  function goBack() {
    if (window.history.length > 1) window.history.back();
    else navigate({ to: '/' });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="native:pt-[calc(env(safe-area-inset-top)+1rem)] mx-auto max-w-2xl px-4 py-6 sm:py-8">
        <button
          type="button"
          onClick={goBack}
          className="text-ink-500 hover:text-ink-900 mb-5 inline-flex min-h-11 items-center gap-1.5 font-sans text-[13px] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
        <p className="text-ink-500 font-sans text-sm">Cet article n&apos;existe pas (ou plus).</p>
      </div>
    );
  }

  const ressources = article.pour_aller_plus_loin;
  const socialLinks = articleSocialLinks(article.social);
  const hasResources =
    ressources.livres.length > 0 ||
    ressources.etudes.length > 0 ||
    ressources.articles.length > 0 ||
    ressources.videos.length > 0;

  return (
    <div className="pb-safe native:pt-[calc(env(safe-area-inset-top)+1rem)] mx-auto max-w-2xl px-4 py-6 sm:py-8">
      {/* Back — sur sa propre ligne, jamais alignée avec le <h1> sur mobile. */}
      <button
        type="button"
        onClick={goBack}
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex min-h-11 items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour au fil
      </button>

      {/* Hero */}
      <div className="flex flex-col gap-4">
        {article.illustration_url ? (
          <div className="relative overflow-hidden rounded-2xl">
            <img
              src={article.illustration_url}
              alt={article.title}
              className="aspect-video w-full object-cover"
            />
            <div
              aria-hidden
              className="bg-linear-to-t pointer-events-none absolute inset-x-0 bottom-0 h-24 from-black/60 to-transparent"
            />
            <div className="absolute right-0 top-0 flex flex-wrap items-end justify-end gap-2 p-3">
              <BlogActiveReaders
                articleId={article.id}
                className="mr-auto inline-flex items-center gap-1.5 rounded-full bg-black/45 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/15 backdrop-blur-sm"
              />
            </div>
            {socialLinks.length > 0 && (
              <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-start justify-start gap-2 p-3">
                <BlogSocialReactions
                  links={socialLinks}
                  variant="overlay"
                  label="Réagissez sur"
                  shareTitle={article.title}
                  shareText={`« ${article.title} » — un parcours de réflexion sur JeRaisonne`}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <BlogActiveReaders
              articleId={article.id}
              className="bg-ink-100 text-ink-700 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold"
            />
            <BlogSocialReactions links={socialLinks} label="Réagissez sur" />
          </div>
        )}
        <div className="text-ink-500 flex flex-wrap items-center gap-2 text-xs">
          {formatBlogDate(article.published_at) && (
            <span className="bg-ink-100 text-ink-700 rounded-full px-3 py-1 font-medium">
              {formatBlogDate(article.published_at)}
            </span>
          )}
          <span className="bg-ink-100 text-ink-700 inline-flex items-center gap-1 rounded-full px-3 py-1 font-medium">
            <Clock className="size-3.5" aria-hidden />
            {article.reading_time} min de lecture
          </span>
          <span className="bg-ink-100 text-ink-700 rounded-full px-3 py-1 font-medium">
            Parcours de réflexion
          </span>
        </div>
        <h1 className="font-display text-ink-900 text-3xl font-extrabold sm:text-4xl">
          {article.title}
        </h1>
        {article.subtitle && <p className="text-ink-600 text-base">{article.subtitle}</p>}
      </div>

      {/* Parcours interactif (blocs + sondages) */}
      <article className="mt-2">
        <ReflectionJourney
          slug={article.slug}
          articleId={article.id}
          articleTitle={article.title}
          blocks={article.blocks}
          conclusionMd={article.conclusion_md}
          socialLinks={socialLinks}
        />
      </article>

      {article.author && <BlogAuthorCard author={article.author} />}
      <p className="text-ink-400 mt-1 text-center text-[11px] italic">* Article généré par IA</p>

      {/* Les penseurs du sujet */}
      {article.penseurs.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-ink-900 mb-4 flex items-center gap-2 text-xl font-bold">
            <Lightbulb className="size-5 text-amber-500" aria-hidden />
            Les penseurs du sujet
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {article.penseurs.map((p, i) => (
              <a
                key={i}
                href={blogPenseurHref(p)}
                target="_blank"
                rel="noreferrer"
                className="hover:border-primary-300 border-ink-200 group block rounded-2xl border bg-white p-4 transition-colors"
              >
                <p className="text-ink-900 flex items-start gap-1.5 text-sm font-bold">
                  <span className="group-hover:underline">{p.name}</span>
                  <ExternalLink className="text-primary-400 mt-0.5 size-3.5 shrink-0" aria-hidden />
                </p>
                <p className="text-ink-500 mt-0.5 text-xs font-medium">{p.position}</p>
                <p className="text-ink-700 mt-2 text-sm">{p.contribution}</p>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Pour aller plus loin */}
      {hasResources && (
        <section className="mt-12">
          <h2 className="font-display text-ink-900 mb-4 text-xl font-bold">Pour aller plus loin</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <ResourceGroup title="Livres" items={ressources.livres} kind="livres" />
            <ResourceGroup title="Études" items={ressources.etudes} kind="etudes" />
            <ResourceGroup title="Articles" items={ressources.articles} kind="articles" />
            <ResourceGroup title="Vidéos" items={ressources.videos} kind="videos" />
          </div>
        </section>
      )}
    </div>
  );
}
